// src/pages/work/WorkDetails.ui.header.jsx
// WorkDetails Header — UI shell (reads user options via hooks, no toasts here)
// - Quote-style actions: Send • Cancel • Set {Next} • Statuses • … More
// - Title row: NO client pill, NO "Job •" prefix before "Last updated"
// - Header cards (lg: 12-col):
//    1) When (Start / End / tz)  [lg:col-span-3, compact]  ← manual save (no autosave)
//    2) Time in status (live)    [lg:col-span-2, compact]
//    3) Priority (color-coded + autosave) [lg:col-span-2]
//    4) Assignees (chips + searchable multi-select) [lg:col-span-5]

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  Briefcase,
  CalendarDays,
  Clock,
  Send,
  CheckCircle2,
  Mail,
  MoreHorizontal,
  Trash2,
} from "lucide-react";

import { useUsers, useUserSearch } from "../../hooks/useUserQueries";
import { MIN_SEARCH_CHARS } from "../../services/userService";

/* --------------------------------- helpers -------------------------------- */
const TYPE_LABEL = { task: "Task", job: "Job", appointment: "Appointment" };
const PRIORITY_LABEL = { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" };

const cx = (...a) =>
  a
    .flatMap((x) =>
      typeof x === "string" ? x : x && typeof x === "object" ? Object.keys(x).filter((k) => x[k]) : []
    )
    .join(" ")
    .trim();

function prettyStatus(s) {
  if (!s) return "";
  return String(s)
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

const fmtDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    : "—";

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
};

const Chip = ({ children, tone = "slate", className }) => (
  <span
    className={cx(
      "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold",
      tone === "slate" && "bg-slate-100 text-slate-800 border-slate-200",
      tone === "indigo" && "bg-indigo-100 text-indigo-800 border-indigo-200",
      tone === "rose" && "bg-rose-100 text-rose-700 border-rose-200",
      tone === "amber" && "bg-amber-100 text-amber-700 border-amber-200",
      className
    )}
  >
    {children}
  </span>
);

const getId = (v) => (typeof v === "string" ? v : v?._id || v?.id || v?.userId || "");
const idStr = (v) => (v == null ? "" : String(v));

const arraysEqualSorted = (a = [], b = []) => {
  const A = a.map(String).slice().sort();
  const B = b.map(String).slice().sort();
  if (A.length !== B.length) return false;
  for (let i = 0; i < A.length; i++) if (A[i] !== B[i]) return false;
  return true;
};

/* ----------------------- AssigneeSelect (header, no button) --------------- */
function AssigneeSelectHeader({
  valueIds = [],
  snapshots = [],
  onAutosave,
  disabled,
  role,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(valueIds);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const boxRef = useRef(null);

  const lastSavedRef = useRef(valueIds.map(String));
  const touchedRef = useRef(false);
  const debRef = useRef(null);

  useEffect(() => {
    setDraft(valueIds);
    lastSavedRef.current = valueIds.map(String);
    touchedRef.current = false;
    setError("");
  }, [valueIds]);

  const { data: baseUsers = [], isLoading: loadingBase } = useUsers({
    includeInvited: true,
    includeInactive: false,
    limit: 50,
  });

  const searching = q.trim().length >= MIN_SEARCH_CHARS;
  const { data: searchedUsers = [], isFetching: loadingSearch } = useUserSearch(q, {
    limit: 20,
    includeInvited: true,
    includeInactive: false,
    enabled: searching,
  });

  const snapshotMap = useMemo(
    () =>
      new Map(
        (snapshots || []).map((s) => {
          const id = idStr(s.id || s._id);
          const name =
            s.name ||
            s.fullName ||
            s.displayName ||
            (s.email ? String(s.email).split("@")[0] : `User ${id.slice(-6)}`);
          return [id, { id, name, avatarUrl: s.avatarUrl || null }];
        })
      ),
    [snapshots]
  );

  const source = searching ? searchedUsers : baseUsers;
  const options = useMemo(() => {
    const norm = (source || []).map((u) => {
      const id = idStr(u.id || u._id);
      const name =
        u.name ||
        u.fullName ||
        u.displayName ||
        (u.email ? String(u.email).split("@")[0] : `User ${id.slice(-6)}`);
      return { id, name, avatarUrl: u.avatarUrl || null };
    });
    draft.forEach((vid) => {
      const sid = idStr(vid);
      if (!norm.some((n) => n.id === sid)) {
        const s = snapshotMap.get(sid);
        norm.unshift({
          id: sid,
          name: s?.name || `User ${sid.slice(-6)}`,
          avatarUrl: s?.avatarUrl || null,
        });
      }
    });
    return norm;
  }, [source, draft, snapshotMap]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, []);

  const saveIfChanged = async (nextDraft) => {
    if (disabled) return;
    if (!touchedRef.current) return;
    if (arraysEqualSorted(nextDraft, lastSavedRef.current)) return;

    try {
      setError("");
      setSaving(true);
      await onAutosave?.(nextDraft);
      lastSavedRef.current = nextDraft.map(String);
    } catch (e) {
      setError(e?.message || "Failed to save assignees");
    } finally {
      setSaving(false);
    }
  };

  const scheduleSave = (nextDraft) => {
    if (disabled) return;
    touchedRef.current = true;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => saveIfChanged(nextDraft), 600);
  };

  const toggle = (id) => {
    if (disabled) return;
    const s = new Set(draft.map(String));
    const k = String(id);
    s.has(k) ? s.delete(k) : s.add(k);
    const next = Array.from(s);
    setDraft(next);
    scheduleSave(next);
  };

  useEffect(() => {
    if (open) return;
    if (debRef.current) {
      clearTimeout(debRef.current);
      debRef.current = null;
    }
    saveIfChanged(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loading = loadingBase || loadingSearch;

  return (
    <div className="relative" ref={boxRef}>
      <div className="mb-2 flex flex-wrap gap-2">
        {draft.length === 0 ? (
          <span className="text-sm text-slate-500">Unassigned</span>
        ) : (
          draft.map((id) => {
            const sid = idStr(id);
            const u = options.find((x) => x.id === sid) || snapshotMap.get(sid);
            const label = u?.name || `User ${sid.slice(-6)}`;
            return (
              <span
                key={sid}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700"
              >
                {u?.avatarUrl ? (
                  <img src={u.avatarUrl} alt="" className="size-5 rounded-full" />
                ) : (
                  <span className="grid size-5 place-items-center rounded-full bg-slate-200 text-[10px] font-medium text-slate-700">
                    {label.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <span className="truncate max-w-[160px]">{label}</span>
                {!disabled && (
                  <button
                    type="button"
                    className="ml-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => toggle(sid)}
                    title="Remove"
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })
        )}
      </div>

      <div className="relative">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          disabled={disabled}
          placeholder="Search users…"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-60"
        />
        {loading && (
          <div className="pointer-events-none absolute inset-y-0 right-2 inline-flex items-center text-slate-400">
            <svg className="size-4 animate-spin" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" opacity="0.2" />
              <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" fill="none" />
            </svg>
          </div>
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No matches</div>
          ) : (
            options.map((u) => {
              const active = draft.map(String).includes(String(u.id));
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className={cx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                    active ? "bg-indigo-50 text-indigo-700" : "hover:bg-slate-50 text-slate-700"
                  )}
                >
                  {u.avatarUrl ? (
                    <img src={u.avatarUrl} alt="" className="size-5 rounded-full" />
                  ) : (
                    <span className="grid size-5 place-items-center rounded-full bg-slate-200 text-[10px] font-medium text-slate-700">
                      {u.name.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className="truncate">{u.name}</span>
                  {active && <span className="ml-auto text-xs">Selected</span>}
                </button>
              );
            })
          )}
        </div>
      )}

      {saving && <div className="mt-1 text-xs text-slate-500">Saving…</div>}
      {error && <div className="mt-1 text-xs text-rose-600">{error}</div>}
    </div>
  );
}

/* ---------------------------------- Header --------------------------------- */
export default function WorkDetailsHeader(props) {
  const {
    work,
    resolving,
    isLoading,
    softDeleted,
    canMutate,

    onBack,
    onOpenEntity,

    primaryNext,
    onTransition,
    transitioning,

    onDelete,
    deleting,

    // Direct-send (legacy / fallback)
    onSendWorkNotice,
    sendingWorkNotice,
    sendingNoticeType,

    // ✅ Preferred: open SendWorkModal from parent
    onRequestSend, // (type: 'confirm'|'reminder'|'complete') => void

    whenLabel: whenLabelFromParent,

    onSaveBasics,       // ({ priority })
    onSaveAssignees,    // (ids: string[])
    onSaveSchedule,     // ({ start, end, allDay, timezone })
    schedule: scheduleProp,

    role,
  } = props;

  const headerLoading = resolving || isLoading;
  const workNumber = work?.workNumber || (work?.id ? `W-${String(work.id).slice(-6)}` : "");

  const allowed = Array.isArray(work?.allowedNextStatuses) ? work.allowedNextStatuses : [];
  const cancelOption = allowed.find((s) => s === "cancelled" || s === "no_show") || null;
  const moreStatusOptions = (primaryNext ? allowed.filter((s) => s !== primaryNext) : [...allowed]).filter(
    (s) => s !== cancelOption
  );

  // live "time in status"
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const timeInStatus = useMemo(() => {
    if (!work?.currentStatusSince) return "";
    const ms = now - new Date(work.currentStatusSince).getTime();
    if (!Number.isFinite(ms) || ms < 0) return "";
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d`;
  }, [now, work?.currentStatusSince]);

  // When (Start / End / tz) — MANUAL save only
  const schedule = scheduleProp || work?.schedule || {};
  const tzStrFromSchedule = schedule?.timezone || "";

  const [whenEditing, setWhenEditing] = useState(false);
  const [startLocal, setStartLocal] = useState(schedule?.start ? toLocalInput(schedule.start) : "");
  const [endLocal, setEndLocal] = useState(schedule?.end ? toLocalInput(schedule.end) : "");
  const [tzLocal, setTzLocal] = useState(tzStrFromSchedule);
  const [allDayLocal, setAllDayLocal] = useState(!!schedule?.allDay);
  const [whenSaving, setWhenSaving] = useState(false);

  // Track last saved snapshot
  const snapshot = (s, e, tz, a) => JSON.stringify({ s, e, tz, a });
  const lastSavedWhenRef = useRef(snapshot(startLocal, endLocal, tzLocal, allDayLocal));

  // Sync from props (no saving)
  useEffect(() => {
    const s = schedule?.start ? toLocalInput(schedule.start) : "";
    const e = schedule?.end ? toLocalInput(schedule.end) : "";
    const tz = schedule?.timezone || "";
    const a = !!schedule?.allDay;

    setStartLocal(s);
    setEndLocal(e);
    setTzLocal(tz);
    setAllDayLocal(a);
    setWhenEditing(false);

    lastSavedWhenRef.current = snapshot(s, e, tz, a);
  }, [schedule?.start, schedule?.end, schedule?.timezone, schedule?.allDay]);

  const whenDirty = useMemo(
    () => snapshot(startLocal, endLocal, tzLocal, allDayLocal) !== lastSavedWhenRef.current,
    [startLocal, endLocal, tzLocal, allDayLocal]
  );

  const handleWhenSave = async () => {
    if (!onSaveSchedule || !canMutate || softDeleted) return;
    if (!whenDirty) return;
    try {
      setWhenSaving(true);
      const timezone =
        tzLocal && tzLocal.trim()
          ? tzLocal.trim()
          : null;

      await onSaveSchedule({
        start: startLocal || null,
        end: endLocal || null,
        allDay: !!allDayLocal,
        timezone,
      });
      lastSavedWhenRef.current = snapshot(startLocal, endLocal, tzLocal, allDayLocal);
      setWhenEditing(false); // close after successful save
    } finally {
      setWhenSaving(false);
    }
  };

  const startStr = schedule?.start ? fmtDateTime(schedule.start) : "—";
  const endStr = schedule?.end ? fmtDateTime(schedule.end) : "—";
  const tzStr = schedule?.timezone || "";
  const whenLabel = useMemo(() => {
    if (whenLabelFromParent) return whenLabelFromParent;
    if (!schedule?.start && !schedule?.end) return "Not scheduled";

    if (schedule?.allDay) {
      const base = `${fmtDate(schedule.start)} (All day)`;
      return tzStr ? `${base} • ${tzStr}` : base;
    }

    const base = `${startStr} – ${endStr}`;
    return tzStr ? `${base} • ${tzStr}` : base;
  }, [whenLabelFromParent, schedule?.start, schedule?.end, schedule?.allDay, tzStr, startStr, endStr]);

  // Priority autosave
  const [priorityLocal, setPriorityLocal] = useState(work?.priority || "normal");
  useEffect(() => setPriorityLocal(work?.priority || "normal"), [work?.priority]);
  const [savingPriority, setSavingPriority] = useState(false);
  const priorityTone =
    priorityLocal === "urgent"
      ? "rose"
      : priorityLocal === "high"
      ? "amber"
      : priorityLocal === "normal"
      ? "indigo"
      : "slate";
  const handlePrioritySave = async (next) => {
    setPriorityLocal(next);
    if (!onSaveBasics || !canMutate || softDeleted) return;
    try {
      setSavingPriority(true);
      await onSaveBasics({ priority: next });
    } finally {
      setSavingPriority(false);
    }
  };

  const selectedAssigneeIds = useMemo(
    () => (Array.isArray(work?.assignees) ? work.assignees.map(getId).filter(Boolean) : []),
    [work?.assignees]
  );
  const assigneeSnapshots = Array.isArray(work?.assigneeSnapshots) ? work.assigneeSnapshots : [];

  // Menus
  const [sendOpen, setSendOpen] = useState(false);
  const sendRef = useRef(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (sendRef.current && !sendRef.current.contains(e.target)) setSendOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, []);

  // Helper: route to modal if available, else direct send
  const triggerSend = (type) => {
    if (!canMutate || softDeleted) return;
    if (typeof onRequestSend === "function") {
      onRequestSend(type);        // open SendWorkModal
    } else {
      onSendWorkNotice?.(type);   // fallback direct send
    }
  };

  return (
    <div className="p-4 sm:p-5 border-b border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-4">
        {/* Left: back + title + chips */}
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
          >
            <ArrowLeft size={16} /> Back
          </button>

          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-slate-900 text-xl sm:text-2xl font-semibold">
                {headerLoading ? "…" : workNumber || "Work"}
              </h1>

              {!!work?.status && <Chip tone="slate">{prettyStatus(work?.status)}</Chip>}
              <Chip tone="indigo">{TYPE_LABEL[work?.type] || "Work"}</Chip>

              {work?.parentWorkId && work?.type !== "job" ? (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-700 hover:bg-slate-100"
                  onClick={() => onOpenEntity?.("work", work.parentWorkId)}
                  title="Open parent job"
                >
                  <Briefcase size={14} /> Parent Job
                </button>
              ) : null}
            </div>

            <p className="text-slate-600 text-sm mt-1">
              Last updated {fmtDateTime(work?.updatedAt || work?.meta?.updatedAt)}
            </p>
          </div>
        </div>

        {/* Right actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Send */}
          <div className="relative" ref={sendRef}>
            <button
              type="button"
              onClick={() => setSendOpen((v) => !v)}
              title="Send email"
              disabled={!canMutate || softDeleted}
              className={cx(
                "inline-flex items-center gap-2 rounded-xl px-3 py-2 font-semibold",
                !canMutate || softDeleted
                  ? "bg-slate-200 text-slate-600 cursor-not-allowed"
                  : "bg-indigo-600 text-white hover:bg-indigo-500"
              )}
            >
              <Send size={16} />
              {sendingWorkNotice ? "Sending…" : "Send"}
              <ChevronDown size={16} className="opacity-90" />
            </button>

            {sendOpen && (
              <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                <button
                  type="button"
                  onClick={() => {
                    setSendOpen(false);
                    triggerSend("confirm");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  <Send size={16} />{" "}
                  {sendingWorkNotice && sendingNoticeType === "confirm"
                    ? "Sending…"
                    : "Send Confirmation"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSendOpen(false);
                    triggerSend("reminder");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  <Mail size={16} />{" "}
                  {sendingWorkNotice && sendingNoticeType === "reminder"
                    ? "Sending…"
                    : "Send Reminder"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSendOpen(false);
                    triggerSend("complete");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                >
                  <CheckCircle2 size={16} />{" "}
                  {sendingWorkNotice && sendingNoticeType === "complete"
                    ? "Sending…"
                    : "Send Completion"}
                </button>
              </div>
            )}
          </div>

          {/* Cancel */}
          {cancelOption && canMutate && !softDeleted && (
            <button
              type="button"
              onClick={() => {
                const ok = window.confirm("Cancel this item?");
                if (ok) onTransition?.(cancelOption);
              }}
              disabled={transitioning}
              className="rounded-xl bg-zinc-700 text-white px-3 py-2 font-semibold hover:bg-black/80 disabled:opacity-60"
              title="Cancel"
            >
              Cancel
            </button>
          )}

          {/* Primary next */}
          {primaryNext && canMutate && !softDeleted && (
            <button
              type="button"
              onClick={() => onTransition?.(primaryNext)}
              disabled={transitioning}
              className="rounded-xl bg-slate-900 text-white px-3 py-2 font-semibold hover:bg-black/90 disabled:opacity-60"
              title={`Change status to ${prettyStatus(primaryNext)}`}
            >
              {transitioning ? "Updating…" : `Set ${prettyStatus(primaryNext)}`}
            </button>
          )}

          {/* More statuses */}
          {moreStatusOptions.length > 0 && canMutate && !softDeleted && (
            <div className="relative" ref={statusRef}>
              <button
                type="button"
                onClick={() => setStatusOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50"
                title="More statuses"
              >
                Statuses <ChevronDown size={16} className="opacity-80" />
              </button>
              {statusOpen && (
                <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                  {moreStatusOptions.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setStatusOpen(false);
                        onTransition?.(s);
                      }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                    >
                      {prettyStatus(s)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* … More */}
          <div className="relative" ref={moreRef}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50"
              title="More"
            >
              <MoreHorizontal size={16} />
            </button>
            {moreOpen && (
              <div className="absolute right-0 z-20 mt-2 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                <button
                  type="button"
                  onClick={() => {
                    setMoreOpen(false);
                    onDelete?.();
                  }}
                  disabled={!canMutate || softDeleted || deleting}
                  className={cx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                    !canMutate || softDeleted
                      ? "text-slate-400 cursor-not-allowed"
                      : "text-rose-600 hover:bg-rose-50"
                  )}
                >
                  <Trash2 size={16} /> {deleting ? "Deleting…" : "Archive"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Header cards */}
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12">
        {/* 1) When (compact, editable; manual Save) */}
        <div className="rounded-xl border border-slate-200 p-3 lg:col-span-3">
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs flex items-center gap-1">
              <CalendarDays size={14} /> When
            </div>
            {onSaveSchedule && canMutate && !softDeleted && (
              <button
                type="button"
                onClick={() => setWhenEditing((v) => !v)}
                className="text-xs text-indigo-700 hover:underline"
                title="Edit schedule"
              >
                {whenEditing ? "Close" : "Edit"}
              </button>
            )}
          </div>

          {!whenEditing ? (
            <>
              {!schedule?.start && !schedule?.end ? (
                <div className="mt-1 text-sm text-slate-900">Not scheduled</div>
              ) : schedule?.allDay ? (
                <>
                  <div className="mt-1 text-sm text-slate-900">
                    All day: {fmtDate(schedule.start)}
                  </div>
                  {tzStr && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      {tzStr}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mt-1 text-sm text-slate-900">
                    <span className="text-slate-500">Start:</span> {startStr}
                  </div>
                  <div className="text-sm text-slate-900">
                    <span className="text-slate-500">End:</span> {endStr}
                  </div>
                  {tzStr && (
                    <div className="text-[11px] text-slate-500 mt-1">
                      {tzStr}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-1 gap-2">
                <label className="text-xs text-slate-600">
                  Start
                  <input
                    type="datetime-local"
                    value={startLocal}
                    onChange={(e) => setStartLocal(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  End
                  <input
                    type="datetime-local"
                    value={endLocal}
                    onChange={(e) => setEndLocal(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  />
                </label>
                <div className="flex items-center justify-between gap-2">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                    <input
                      type="checkbox"
                      checked={allDayLocal}
                      onChange={(e) => setAllDayLocal(e.target.checked)}
                      className="size-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    All day
                  </label>
                  <label className="text-xs text-slate-600 grow">
                    Timezone
                    <input
                      type="text"
                      value={tzLocal}
                      onChange={(e) => setTzLocal(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      placeholder="e.g. America/Toronto"
                    />
                  </label>
                </div>

                {/* Save button (small) */}
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleWhenSave}
                    disabled={!whenDirty || whenSaving || !canMutate || softDeleted}
                    className={cx(
                      "inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-semibold",
                      !whenDirty || whenSaving || !canMutate || softDeleted
                        ? "bg-slate-200 text-slate-600 cursor-not-allowed"
                        : "bg-slate-900 text-white hover:bg-black/90"
                    )}
                    title="Save schedule"
                  >
                    {whenSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 2) Time in status */}
        <div className="rounded-xl border border-slate-200 p-3 lg:col-span-2">
          <div className="text-slate-500 text-xs flex items-center gap-1">
            <Clock size={14} /> Time in status
          </div>
          <div className="mt-1 text-base sm:text-lg font-semibold text-slate-900">
            {timeInStatus || "—"}
          </div>
        </div>

        {/* 3) Priority */}
        <div className="rounded-xl border border-slate-200 p-3 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div className="text-slate-500 text-xs">Priority</div>
            <Chip tone={priorityTone}>{PRIORITY_LABEL[priorityLocal] || "—"}</Chip>
          </div>

          {onSaveBasics && canMutate && !softDeleted ? (
            <div className="mt-2">
              <select
                value={priorityLocal}
                onChange={(e) => handlePrioritySave(e.target.value)}
                disabled={savingPriority}
                className={cx(
                  "w-full rounded-lg border px-3 py-2 text-sm text-slate-900 disabled:opacity-60",
                  "bg-white border-slate-200",
                  priorityLocal === "urgent" && "ring-2 ring-rose-200",
                  priorityLocal === "high" && "ring-2 ring-amber-200",
                  priorityLocal === "normal" && "ring-2 ring-indigo-200",
                  priorityLocal === "low" && "ring-2 ring-slate-200"
                )}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              {savingPriority && <div className="mt-1 text-xs text-slate-500">Saving…</div>}
            </div>
          ) : (
            <div className="mt-2 text-lg font-semibold text-slate-900">
              {PRIORITY_LABEL[work?.priority] || "—"}
            </div>
          )}
        </div>

        {/* 4) Assignees */}
        <div className="rounded-xl border border-slate-200 p-4 lg:col-span-5">
          <div className="text-slate-500 text-xs">Assignees</div>
          <div className="mt-1">
            <AssigneeSelectHeader
              valueIds={selectedAssigneeIds}
              snapshots={assigneeSnapshots}
              disabled={!canMutate || softDeleted}
              role={role}
              onAutosave={onSaveAssignees}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
